import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { UIExportModal } from "../../../../ui";
import messages from "../../../../../assets/locale/messages";
import { workingLogsColumns } from "../../../../../utils/Constants";

/**
 * Grid order. The 4-column grid fills row by row, so this list zig-zags across
 * the design's columns to render the exact layout it specifies:
 *
 *   Full Name in Arabic  | Nationality         | Date of birth        | Payroll/Day
 *   Full Name in English | Self-CheckOut Dates | Stc Number           | Expected Payroll
 *   Role                 | Mobile Number       | No. of Working Days  | Iban
 *   National ID/Iqama ID | Email               | Total Working Hours  | Signature
 */
const EXPORT_FIELDS = [
  { name: workingLogsColumns.dailyNameAr, labelKey: "nameAr" },
  { name: workingLogsColumns.dailyNationality, labelKey: "nationality" },
  { name: workingLogsColumns.dailyDobHijri, labelKey: "birthDate" },
  { name: workingLogsColumns.dailyExpectedPayroll, labelKey: "payrollDay" },

  { name: workingLogsColumns.dailyNameEn, labelKey: "nameEn" },
  { name: workingLogsColumns.selfCheckoutDates, labelKey: "selfCheckoutDates" },
  { name: workingLogsColumns.stcNumber, labelKey: "stcNumber" },
  { name: workingLogsColumns.dailyTotalExpectedPayroll, labelKey: "expectedPayroll" },

  { name: workingLogsColumns.dailyRole, labelKey: "role" },
  { name: workingLogsColumns.dailyPhoneNumber, labelKey: "mobile" },
  { name: workingLogsColumns.dailyNumberOfDays, labelKey: "workingDays" },
  { name: workingLogsColumns.dailyIban, labelKey: "iban" },

  { name: workingLogsColumns.dailyIdentificationId, labelKey: "nationalIqama" },
  { name: workingLogsColumns.dailyEmail, labelKey: "email" },
  { name: workingLogsColumns.dailyWorkingHours, labelKey: "totalWorkingHours" },
  { name: workingLogsColumns.dailySignature, labelKey: "signature" },
];

/**
 * The order the columns appear in the generated file — the design's grid read
 * top-to-bottom, column by column. Kept separate from the grid order above so
 * the layout and the file's column order can't drift into each other.
 */
const EXPORT_COLUMN_ORDER = [
  workingLogsColumns.dailyNameAr,
  workingLogsColumns.dailyNameEn,
  workingLogsColumns.dailyRole,
  workingLogsColumns.dailyIdentificationId,
  workingLogsColumns.dailyNationality,
  workingLogsColumns.selfCheckoutDates,
  workingLogsColumns.dailyPhoneNumber,
  workingLogsColumns.dailyEmail,
  workingLogsColumns.dailyDobHijri,
  workingLogsColumns.stcNumber,
  workingLogsColumns.dailyNumberOfDays,
  workingLogsColumns.dailyWorkingHours,
  workingLogsColumns.dailyExpectedPayroll,
  workingLogsColumns.dailyTotalExpectedPayroll,
  workingLogsColumns.dailyIban,
  workingLogsColumns.dailySignature,
];

const buildEmptySelection = () =>
  Object.fromEntries(EXPORT_FIELDS.map(({ name }) => [name, false]));

/**
 * Column selector for the aggregated working-log export, shared by both portals.
 * Hands the caller the picked columns as `{ [column_name]: order_index }` — the
 * shape `/working_logs/export_report` expects — plus those columns' definitions
 * in the same order, for callers that also need the labels.
 */
const ExportTimesheetModal = ({ open, loading, onClose, onConfirm }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events, timeSheet, shared } = messages[lang];
  const copy = events.viewEvents.timesheetTab.exportModal;

  const fields = useMemo(
    () =>
      EXPORT_FIELDS.map(({ name, labelKey }) => ({
        name,
        label: timeSheet.dailyExportSheet[labelKey],
      })),
    [timeSheet],
  );

  const [selectedFields, setSelectedFields] = useState(buildEmptySelection);

  useEffect(() => {
    if (!open) return;
    setSelectedFields(buildEmptySelection());
  }, [open]);

  const handleFieldChange = (name, checked) => {
    setSelectedFields((current) => ({ ...current, [name]: checked }));
  };

  const handleSelectAll = (checked) => {
    setSelectedFields(
      Object.fromEntries(EXPORT_FIELDS.map(({ name }) => [name, checked])),
    );
  };

  const selectedCount = fields.filter((field) => selectedFields[field.name]).length;
  const isAllSelected = selectedCount === fields.length;
  const isIndeterminate = selectedCount > 0 && !isAllSelected;

  const handleConfirm = () => {
    const columns = {};
    const selectedDefs = EXPORT_COLUMN_ORDER.filter(
      (name) => selectedFields[name],
    ).map((name, index) => {
      columns[name] = index;
      return fields.find((field) => field.name === name);
    });

    onConfirm(columns, selectedDefs);
  };

  return (
    <UIExportModal
      open={open}
      fieldsColumns={4}
      maxWidth="xl"
      title={copy.title}
      description={copy.description}
      selectAllLabel={copy.selectAll}
      cancelLabel={shared.cancel}
      confirmLabel={copy.confirmExport}
      fields={fields}
      selectedFields={selectedFields}
      isAllSelected={isAllSelected}
      isIndeterminate={isIndeterminate}
      confirmDisabled={selectedCount === 0 || loading}
      onClose={onClose}
      onCancel={onClose}
      onConfirm={handleConfirm}
      onFieldChange={handleFieldChange}
      onSelectAll={handleSelectAll}
    />
  );
};

ExportTimesheetModal.propTypes = {
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

ExportTimesheetModal.defaultProps = {
  loading: false,
};

export default ExportTimesheetModal;
